/**
 * usePublicArtifactDetail
 *
 * Lazily fetches the full detail DTO for the currently selected public
 * artifact. The slim LIST/MAP payloads carry only what their rows/pins render;
 * the heavy task/note/obstruction detail is loaded on demand here when the URL
 * selects an artifact (via {@link PublicArtifactDescriptor}).
 *
 * Store-free — uses {@link publicShareService} only. A cancellation flag guards
 * against races when the selection changes mid-flight.
 *
 * @module components/share/public/usePublicArtifactDetail
 */
import { useEffect, useState } from 'react';

import { publicShareService } from '@/services/api';
import type { PublicNote, PublicObstruction, PublicTask } from '@/types/share';

import type {
  PublicArtifactDescriptor,
  PublicArtifactKind,
} from './usePublicArtifactSelection';

/**
 * Discriminated union pairing the loaded detail DTO with its kind so consumers
 * narrow `detail` to the concrete type without casts.
 */
export type PublicArtifactDetailSlot =
  | { kind: 'task'; detail: PublicTask }
  | { kind: 'note'; detail: PublicNote }
  | { kind: 'obstruction'; detail: PublicObstruction };

interface UsePublicArtifactDetailResult {
  artifact: PublicArtifactDetailSlot | null;
  isDetailLoading: boolean;
  detailError: boolean;
}

/**
 * Fetches the detail DTO for the given artifact kind and returns a narrowed
 * slot. Each branch awaits the kind-specific service call so the returned
 * literal carries the concrete DTO type — building the slot from the raw union
 * would not type-check.
 */
async function fetchArtifactSlot(
  token: string,
  kind: PublicArtifactKind,
  id: string
): Promise<PublicArtifactDetailSlot> {
  switch (kind) {
    case 'task': {
      const response = await publicShareService.getTaskDetail(token, id);
      return { kind: 'task', detail: response.data };
    }
    case 'note': {
      const response = await publicShareService.getNoteDetail(token, id);
      return { kind: 'note', detail: response.data };
    }
    case 'obstruction': {
      const response = await publicShareService.getObstructionDetail(token, id);
      return { kind: 'obstruction', detail: response.data };
    }
    default: {
      const exhaustiveCheck: never = kind;
      throw new Error(
        `Unknown public artifact kind: ${String(exhaustiveCheck)}`
      );
    }
  }
}

export function usePublicArtifactDetail(
  token: string,
  descriptor: PublicArtifactDescriptor | null
): UsePublicArtifactDetailResult {
  const [artifact, setArtifact] = useState<PublicArtifactDetailSlot | null>(
    null
  );
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);

  // Key the effect on the descriptor's identity PRIMITIVES, not its object
  // reference: a viewer-filter refetch replaces `listData`, which makes
  // `usePublicArtifactSelection` return a fresh descriptor object even when the
  // open artifact is unchanged. Depending on the object would re-fire a
  // redundant detail fetch (loading-skeleton flash) on every filter change.
  const descriptorKind = descriptor?.kind ?? null;
  const descriptorId = descriptor?.id ?? null;

  useEffect(() => {
    if (!descriptorKind || !descriptorId) {
      setArtifact(null);
      setIsDetailLoading(false);
      setDetailError(false);
      return undefined;
    }

    let cancelled = false;
    setIsDetailLoading(true);
    setDetailError(false);

    fetchArtifactSlot(token, descriptorKind, descriptorId)
      .then((slot) => {
        if (cancelled) return;
        setArtifact(slot);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Failed to load public artifact detail:', error);
        setArtifact(null);
        setDetailError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setIsDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, descriptorKind, descriptorId]);

  return { artifact, isDetailLoading, detailError };
}
